export const DEFAULT_STALE_TIME = 30_000

const store = new Map()

const keyFor = (path, payload) =>
  payload ? `${path}::${JSON.stringify(payload)}` : path

const getEntry = (key) => {
  let entry = store.get(key)
  if (!entry) {
    entry = { data: undefined, error: undefined, updatedAt: null, promise: null, subscribers: new Set() }
    store.set(key, entry)
  }
  return entry
}

const subscribe = (key, cb) => {
  const entry = getEntry(key)
  entry.subscribers.add(cb)
  return () => entry.subscribers.delete(cb)
}

const notify = (key, reason) => {
  const entry = store.get(key)
  if (!entry) return
  entry.subscribers.forEach((cb) => cb(reason))
}

const isStale = (key, staleTime) => {
  const entry = store.get(key)
  if (!entry || !entry.updatedAt) return true
  return Date.now() - entry.updatedAt > staleTime
}

// Dedupes concurrent requests for the same key — the in-flight promise is
// stored synchronously (before awaiting `fetcher`) so same-tick callers share it.
const fetchAndStore = (key, fetcher) => {
  const entry = getEntry(key)
  if (entry.promise) return entry.promise

  const promise = fetcher()
    .then(({ data, error }) => {
      const current = getEntry(key)
      current.data = error ? current.data : data
      current.error = error
      current.updatedAt = Date.now()
      current.promise = null
      notify(key, 'settled')
    })
    .catch(() => {
      const current = getEntry(key)
      current.promise = null
    })

  entry.promise = promise
  return promise
}

const invalidate = (key) => {
  const entry = store.get(key)
  if (!entry) return
  entry.updatedAt = 0
  notify(key, 'invalidated')
}

const invalidateByPrefix = (prefix) => {
  store.forEach((_entry, key) => {
    if (key.startsWith(prefix)) invalidate(key)
  })
}

const invalidateWhere = (predicate) => {
  store.forEach((_entry, key) => {
    if (predicate(key)) invalidate(key)
  })
}

const invalidateMany = (target) => {
  if (typeof target === 'function') return invalidateWhere(target)
  const keys = Array.isArray(target) ? target : [target]
  keys.forEach((k) => invalidate(k))
}

const clear = () => store.clear()

export const queryCache = {
  keyFor,
  getEntry,
  subscribe,
  notify,
  isStale,
  fetchAndStore,
  invalidate,
  invalidateByPrefix,
  invalidateWhere,
  invalidateMany,
  clear
}
